import type { ReactNode } from 'react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * Sobreposicao ANCORADA a um elemento.
 *
 * Diferente de `Modal` (centro da tela) e de `Drawer` (borda): aqui a
 * sobreposicao nasce colada na linha que a abriu. Vai em portal no `body`
 * porque uma coluna com rolagem ou `overflow: hidden` cortaria o conteudo se
 * ele ficasse dentro dela.
 *
 * A medicao acontece ANTES da pintura (`useLayoutEffect`): posicionar depois
 * faria a sobreposicao aparecer num canto e pular para o lugar certo.
 */

/**
 * `esquerda`/`direita`: ao lado da âncora, alinhado pelo topo (menus de linha).
 * `acima`: em cima da âncora, alinhado pela borda esquerda — para botões no
 * rodapé (ex.: o `+` do compositor), sem cobrir o que está embaixo.
 */
export type LadoAncorado = 'esquerda' | 'direita' | 'acima'

export type AnchoredOverlayProps = {
  /** Elemento ao qual colar. Quando null, nada e desenhado. */
  anchor: HTMLElement | null
  onClose: () => void
  /** Lado preferido; vira o oposto sozinho quando nao couber. */
  prefer?: LadoAncorado
  /** Distancia entre a ancora e a sobreposicao, em px. */
  gap?: number
  role?: 'dialog' | 'menu'
  'aria-label'?: string
  className?: string
  /**
   * Camada em que a sobreposicao vive. O scrim vai uma abaixo.
   *
   * Existe porque quem abre um popover DENTRO de uma tela empilhada alto (a
   * chamada de video vive em 1200) costumava subir so o cartao, pelo CSS. O
   * scrim ficava embaixo da tela, o clique de fora nunca chegava nele e o
   * popover so fechava pelo Esc. Subir os dois juntos e o unico jeito de o
   * "clicar fora fecha" continuar valendo.
   */
  zIndex?: number
  children: ReactNode
}

/** Folga minima da borda da janela, para nao colar no limite da tela. */
const MARGEM_JANELA = 8

export function AnchoredOverlay({
  anchor,
  onClose,
  prefer = 'esquerda',
  gap = 8,
  role = 'dialog',
  className,
  zIndex,
  children,
  ...rest
}: AnchoredOverlayProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [posicao, setPosicao] = useState<{ top: number; left: number } | null>(null)

  // onClose costuma ser arrow recriada a cada render; o ref evita reassinar os
  // ouvintes a cada render do chamador (mesmo motivo do overlay.tsx).
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useLayoutEffect(() => {
    if (!anchor || !ref.current) return

    const posicionar = () => {
      const caixa = ref.current
      if (!caixa || !anchor) return
      const ancora = anchor.getBoundingClientRect()
      const largura = caixa.offsetWidth
      const altura = caixa.offsetHeight

      if (prefer === 'acima') {
        // Em cima da âncora; se não couber no topo da janela, desce para baixo.
        const acima = ancora.top - gap - altura
        const top = acima >= MARGEM_JANELA ? acima : ancora.bottom + gap
        const leftMaximo = window.innerWidth - largura - MARGEM_JANELA
        setPosicao({ top, left: Math.max(MARGEM_JANELA, Math.min(ancora.left, leftMaximo)) })
        return
      }

      // Cabe do lado preferido? Se nao, vai para o outro. Se nao couber em
      // nenhum, fica no que tiver mais espaco.
      const espacoEsquerda = ancora.left - gap - MARGEM_JANELA
      const espacoDireita = window.innerWidth - ancora.right - gap - MARGEM_JANELA
      const preferEsquerda = prefer === 'esquerda'
      const cabePreferido = preferEsquerda ? espacoEsquerda >= largura : espacoDireita >= largura
      const cabeOposto = preferEsquerda ? espacoDireita >= largura : espacoEsquerda >= largura

      let usarEsquerda: boolean
      if (cabePreferido) usarEsquerda = preferEsquerda
      else if (cabeOposto) usarEsquerda = !preferEsquerda
      else usarEsquerda = espacoEsquerda >= espacoDireita

      const left = usarEsquerda ? ancora.left - gap - largura : ancora.right + gap

      // Alinha pelo topo da linha, mas nunca deixa sair da janela.
      const topMaximo = window.innerHeight - altura - MARGEM_JANELA
      const top = Math.max(MARGEM_JANELA, Math.min(ancora.top, topMaximo))

      setPosicao({ top, left: Math.max(MARGEM_JANELA, left) })
    }

    posicionar()

    // Rolagem em qualquer ancestral e redimensionamento movem a ancora: sem
    // reposicionar, a sobreposicao fica flutuando longe da linha.
    window.addEventListener('scroll', posicionar, true)
    window.addEventListener('resize', posicionar)
    return () => {
      window.removeEventListener('scroll', posicionar, true)
      window.removeEventListener('resize', posicionar)
    }
  }, [anchor, gap, prefer])

  useEffect(() => {
    if (!anchor) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onCloseRef.current()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [anchor])

  if (!anchor) return null

  return createPortal(
    <>
      {/* Scrim transparente: fecha ao clicar fora sem escurecer a tela, que e
          o comportamento de popover, nao de modal. */}
      <div
        className="ob-anchored__scrim"
        onMouseDown={onClose}
        aria-hidden
        style={zIndex === undefined ? undefined : { zIndex: zIndex - 1 }}
      />
      <div
        ref={ref}
        role={role}
        aria-label={rest['aria-label']}
        className={['ob-anchored', className].filter(Boolean).join(' ')}
        style={{
          top: posicao?.top ?? 0,
          left: posicao?.left ?? 0,
          ...(zIndex === undefined ? {} : { zIndex }),
          // Antes da primeira medicao a caixa existe mas nao tem lugar: sem
          // isto ela pisca no canto superior esquerdo.
          visibility: posicao ? 'visible' : 'hidden',
        }}
      >
        {children}
      </div>
    </>,
    document.body,
  )
}
